/**
 * src/components/billing/PaymentWarningBanner.jsx
 *
 * App-shell banner shown to ALL authenticated users when the tenant has
 * overdue invoices. The banner sits above the TopBar and sidebar so it is
 * visible regardless of which route the user is on.
 *
 * Severity tiers (based on oldest overdue invoice):
 *   < 30 days  — yellow  (warn)   : "Payment overdue — admin please resolve"
 *   30–59 days — orange  (warn+)  : "Action required — service may be affected"
 *   ≥ 60 days  — red     (danger) : "Account at risk of suspension"
 *
 * Non-admin users see the banner but not the "view invoices" link (they
 * cannot access billing details — BILL-B-005). Admins get a direct link to
 * Settings → Billing.
 *
 * Dismiss: the banner dismisses per-session only (sessionStorage). It
 * re-appears on the next login or if the polling detects the overdue status
 * persists after a dismiss. Paying the invoice clears the overdue state
 * server-side; the next 5-minute poll removes the banner automatically.
 */

import { useState, useCallback } from 'react';
import { usePaymentStatus } from '../../hooks/usePaymentStatus.js';
import { canAdmin } from '../../utils/auth.js';

const DISMISS_KEY = 'payment_banner_dismissed_v1';

function getThreshold(oldestOverdueDays) {
  if (oldestOverdueDays === null || oldestOverdueDays === undefined) return 'warn';
  if (oldestOverdueDays >= 60) return 'danger';
  if (oldestOverdueDays >= 30) return 'critical';
  return 'warn';
}

const TIER_STYLES = {
  warn: {
    background: 'var(--warn-bg)',
    borderBottom: '1px solid var(--warn)',
    color: 'var(--warn)',
  },
  critical: {
    background: 'rgba(244, 120, 50, 0.12)',
    borderBottom: '1px solid #f47832',
    color: '#f47832',
  },
  danger: {
    background: 'var(--danger-bg)',
    borderBottom: '1px solid var(--danger)',
    color: 'var(--danger)',
  },
};

const TIER_MESSAGES = {
  warn: 'A payment is overdue on this account.',
  critical: 'A payment is overdue. Please resolve to avoid service disruption.',
  danger: 'Account at risk of suspension — payment overdue for 60+ days.',
};

export default function PaymentWarningBanner({ tenantId, session }) {
  const { hasOverdue, overdueCount, oldestOverdueDays, loading } = usePaymentStatus(
    tenantId,
    session
  );
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');

  const dismiss = useCallback(() => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  }, []);

  if (loading || !hasOverdue || dismissed) return null;

  const tier = getThreshold(oldestOverdueDays);
  const isAdmin = canAdmin(session?.role);

  return (
    <div
      role="alert"
      style={{
        ...TIER_STYLES[tier],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 20px',
        fontSize: '13px',
        fontFamily: 'var(--font-mono)',
        gap: 12,
        zIndex: 200,
        flexShrink: 0,
        pointerEvents: 'auto',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 600 }}>
          {overdueCount > 1 ? `${overdueCount} invoices overdue` : 'Invoice overdue'}
        </span>
        {' — '}
        {TIER_MESSAGES[tier]}
        {isAdmin && (
          <>
            {' '}
            <a
              href="/settings?tab=billing"
              style={{
                color: 'inherit',
                textDecoration: 'underline',
                fontWeight: 600,
                marginLeft: 4,
              }}
            >
              View invoices →
            </a>
          </>
        )}
      </span>
      <button
        onClick={dismiss}
        aria-label="Dismiss payment warning"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'inherit',
          fontSize: 18,
          lineHeight: 1,
          padding: '0 4px',
          opacity: 0.7,
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}
